import {redirectToLoginForUnauthorized} from '../auth/redirect-to-login'
import {ApiError, UnauthorizedError} from './errors'

const API_BASE_PATH = '/api'

/** Absolute URL that ignores userinfo embedded in the page URL (e.g. no-reply@depot.mattechus.com). */
export function apiUrl(path: string): string {
    return new URL(`${API_BASE_PATH}${path}`, window.location.origin).href
}

function apiRequestHeaders(init?: RequestInit): Record<string, string> {
    const headers: Record<string, string> = {
        ...(init?.headers as Record<string, string>),
    }

    // Skip Content-Type header for FormData (let browser set multipart/form-data with boundary)
    if (!(init?.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json'
    }

    return headers
}

const SERVER_PROBLEM = 'Server error. Reload the page to see what was saved'

async function parseErrorBody(res: Response): Promise<unknown> {
    try {
        return await res.json()
    } catch (error) {
        if (error instanceof SyntaxError) {
            return null
        }

        throw error
    }
}

async function parseJsonResponse(res: Response): Promise<unknown> {
    try {
        return await res.json()
    } catch (error) {
        if (error instanceof SyntaxError) {
            if (res.status >= 502 && res.status <= 504) {
                throw new Error('API server is unavailable. Is the backend running?', {
                    cause: error,
                })
            }
            throw new Error(SERVER_PROBLEM, {cause: error})
        }
        throw error
    }
}

function apiErrorMessage(res: Response, body: unknown): string {
    if (body != null && typeof body === 'object' && 'detail' in body) {
        const detail = (body as {detail?: unknown}).detail
        if (typeof detail === 'string' && detail.trim()) {
            return detail
        }
        if (detail != null && typeof detail === 'object' && 'message' in detail) {
            const message = (detail as {message?: unknown}).message
            if (typeof message === 'string' && message.trim()) {
                return message
            }
        }
        if (Array.isArray(detail)) {
            const messages = detail.flatMap((item) => {
                if (item == null || typeof item !== 'object' || !('msg' in item)) {
                    return []
                }
                const message = (item as {msg?: unknown}).msg
                // Pydantic prefixes validator messages with "Value error, "
                return typeof message === 'string' && message.trim()
                    ? [message.replace(/^Value error, /, '')]
                    : []
            })
            if (messages.length > 0) {
                return messages.join('; ')
            }
        }
    }

    if (res.status >= 502 && res.status <= 504) {
        return 'API server is unavailable. Is the backend running?'
    }

    if (res.status >= 500) {
        return SERVER_PROBLEM
    }

    return `API error: ${res.status}`
}

async function handleUnauthorizedResponse(path: string, res: Response): Promise<never> {
    let reason: UnauthorizedError['reason']

    try {
        const body = (await res.json()) as {detail?: {reason?: string}}
        const detailReason = body.detail?.reason
        if (
            detailReason === 'invalid' ||
            detailReason === 'expired' ||
            detailReason === 'consumed'
        ) {
            reason = detailReason
        }
    } catch (error) {
        if (!(error instanceof SyntaxError)) {
            throw error
        }
    }

    const error = new UnauthorizedError(reason)
    if (path !== '/auth/me') {
        redirectToLoginForUnauthorized(error)
    }
    throw error
}

async function fetchApi(path: string, init?: RequestInit): Promise<Response> {
    const res = await fetch(apiUrl(path), {
        ...init,
        credentials: 'include',
        headers: apiRequestHeaders(init),
    })

    if (res.status === 401) {
        return handleUnauthorizedResponse(path, res)
    }

    if (!res.ok) {
        const body = await parseErrorBody(res)
        throw new ApiError(apiErrorMessage(res, body), res.status, body)
    }

    return res
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await fetchApi(path, init)

    if (res.status === 204) {
        return undefined as T
    }

    const body = await parseJsonResponse(res)
    return body as T
}

export function filenameFromContentDisposition(contentDisposition: string | null): string | null {
    if (contentDisposition == null) {
        return null
    }

    const quotedMatch = contentDisposition.match(/filename="([^"]+)"/i)
    if (quotedMatch?.[1]) {
        return quotedMatch[1]
    }

    const unquotedMatch = contentDisposition.match(/filename=([^;\s]+)/i)
    return unquotedMatch?.[1] ?? null
}

export async function apiFetchBlob(path: string, init?: RequestInit): Promise<Blob> {
    const res = await fetchApi(path, init)
    return res.blob()
}

export async function downloadApiBlob(
    path: string,
    fallbackFilename: string,
    init?: RequestInit,
): Promise<void> {
    const res = await fetchApi(path, init)
    const blob = await res.blob()
    const filename =
        filenameFromContentDisposition(res.headers.get('Content-Disposition')) ?? fallbackFilename
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
}
